import { useState } from 'react';
import {
  VegaButton,
  VegaCard,
  VegaFlex,
  VegaFont,
  VegaGrid,
  VegaPageNotification,
  VegaTable,
} from '@globalpayments/vega-react';
import type {
  VegaTableColumnConfig,
  VegaTableDataSourceItem,
} from '@globalpayments/vega/dist/types/components/vega-table/interface';
import { formatInTimeZone } from 'date-fns-tz';

import DetailsModal from '../../../components/detailsModal';
import type {
  HorizonOptOutDateRange,
  HorizonOptOutDisplayRow,
  HorizonOptOutType,
  HorizonRegion,
} from '../../../types/horizonOptOutTracker';
import { createHorizonOptOutCsv } from '../../../utility/horizonOptOutTracker';
import { chipColors, tablePagination } from '../constants';

interface HorizonOptOutTableProps {
  dateRange: HorizonOptOutDateRange;
  hasSearchFilter: boolean;
  isEnriching: boolean;
  rows: HorizonOptOutDisplayRow[];
}

interface HorizonOptOutTableRecord extends VegaTableDataSourceItem {
  appUid: string;
  companyName: string;
  email: string;
  optOutReason: string;
  optOutTime: string;
  region: string;
  type: string;
}

const isRegion = (value: unknown): value is HorizonRegion => value === 'US' || value === 'UK';
const isOptOutType = (value: unknown): value is HorizonOptOutType => value === 'Worth' || value === 'Global Payments';

const createChip = (
  createElement: Parameters<NonNullable<VegaTableColumnConfig['render']>>[0],
  value: HorizonRegion | HorizonOptOutType,
  ariaLabel: string = value,
) =>
  createElement('vega-chip', {
    'aria-label': ariaLabel,
    bgColor: chipColors[value].bgColor,
    chipType: 'label',
    size: 'extra-small',
    text: value,
    textColor: chipColors[value].textColor,
    variant: 'filled-background',
  });

const baseColumns: VegaTableColumnConfig[] = [
  {
    key: 'email',
    label: 'Email',
    prop: 'email',
    overflow: 'ellipsis',
    ellipsisOptions: { showTooltip: true },
    width: '240px',
  },
  {
    key: 'companyName',
    label: 'Company name',
    prop: 'companyName',
    overflow: 'ellipsis',
    ellipsisOptions: { showTooltip: true },
    width: '220px',
  },
  {
    key: 'region',
    label: 'Region',
    prop: 'region',
    width: '100px',
    render: (createElement, value) =>
      isRegion(value) ? createChip(createElement, value, `Horizon ${value}`) : String(value ?? 'N/A'),
  },
  {
    key: 'optOutReason',
    label: 'Opt out reason',
    prop: 'optOutReason',
    overflow: 'ellipsis',
    ellipsisOptions: { showTooltip: true },
    width: '360px',
  },
  {
    key: 'type',
    label: 'Type',
    prop: 'type',
    width: '160px',
    render: (createElement, value) => (isOptOutType(value) ? createChip(createElement, value) : String(value ?? 'N/A')),
  },
  { key: 'optOutTime', label: 'Time of opt out (UTC)', prop: 'optOutTime', width: '200px' },
];

const createTableData = (rows: HorizonOptOutDisplayRow[]): HorizonOptOutTableRecord[] =>
  rows.map((row) => ({
    appUid: row.appUid,
    companyName: row.companyName,
    email: row.email,
    optOutReason:
      row.detailStatus === 'loading'
        ? 'Loading...'
        : row.detailStatus === 'error'
          ? 'Unable to load'
          : (row.optOutReason ?? 'N/A'),
    optOutTime:
      row.detailStatus === 'loaded' && row.optOutTimeUTC
        ? formatInTimeZone(row.optOutTimeUTC, 'UTC', 'dd MMM yyyy, HH:mm')
        : 'N/A',
    region: row.region,
    type: row.detailStatus === 'loaded' ? (row.type ?? 'N/A') : 'N/A',
  }));

const downloadCsv = (rows: HorizonOptOutTableRecord[], dateRange: HorizonOptOutDateRange) => {
  const csvUrl = URL.createObjectURL(
    new Blob([`\uFEFF${createHorizonOptOutCsv(rows)}`], { type: 'text/csv;charset=utf-8' }),
  );
  const downloadLink = document.createElement('a');
  downloadLink.download = `horizon-opt-outs-${dateRange.startDate}-to-${dateRange.endDate}.csv`;
  downloadLink.href = csvUrl;
  document.body.append(downloadLink);
  downloadLink.click();
  downloadLink.remove();
  window.setTimeout(() => URL.revokeObjectURL(csvUrl), 0);
};

/** Displays paginated tracker results with CSV export and application details. */
export const HorizonOptOutTable = ({ dateRange, hasSearchFilter, isEnriching, rows }: HorizonOptOutTableProps) => {
  const [detailsAppUid, setDetailsAppUid] = useState<string>();
  const tableData = createTableData(rows);
  const detailsRow = rows.find((row) => row.appUid === detailsAppUid);
  const columns: VegaTableColumnConfig[] = [
    ...baseColumns,
    {
      key: 'details',
      label: 'Details',
      width: '140px',
      render: (createElement, _value, record) =>
        createElement('vega-button-link', {
          'aria-label': `View technical details for ${String(record.companyName)}`,
          label: 'View details',
          onVegaClick: () => setDetailsAppUid(String(record.appUid)),
          size: 'small',
        }),
    },
  ];

  return (
    <VegaCard padding={{ default: 'size-16', M: 'size-24' }} variant="border">
      <VegaFlex direction="col" gap="size-16">
        {isEnriching && (
          <VegaFont as="p" color="text-secondary" variant="font-p2-short">
            Loading opt-out details for the selected date range...
          </VegaFont>
        )}
        {rows.length === 0 ? (
          !isEnriching && (
            <VegaPageNotification
              duration={0}
              message={
                hasSearchFilter
                  ? 'No opt-outs match the applied search filters.'
                  : 'No disqualified Horizon applications were found.'
              }
              notificationTitle={hasSearchFilter ? 'No matching opt-outs' : 'No applications found'}
              type="info"
            />
          )
        ) : (
          <>
            <VegaFlex justifyContent="end">
              <VegaButton
                disabled={isEnriching}
                icon="fa-solid fa-download"
                label="Export CSV"
                onVegaClick={() => downloadCsv(tableData, dateRange)}
                size="small"
                variant="secondary"
              />
            </VegaFlex>
            <VegaTable
              aria-label="Horizon opt-outs"
              columns={columns}
              dataSource={tableData}
              density="compact"
              pagination={tablePagination}
              rowClickable={false}
              rowDivider
              rowKey="appUid"
              striped
            />
          </>
        )}
        <DetailsModal
          modalTitle={detailsRow ? `${detailsRow.companyName} details` : 'Application details'}
          onClose={() => setDetailsAppUid(undefined)}
          open={detailsRow !== undefined}
          width={{ default: '90%', M: '50%' }}>
          {detailsRow && (
            <VegaGrid column={{ default: 1, M: 2 }} gap="size-16">
              <VegaFlex direction="col" gap="size-4">
                <VegaFont as="span" color="text-secondary" variant="font-field-label-em">
                  Company
                </VegaFont>
                <VegaFont as="span" variant="font-p2-short">
                  {detailsRow.companyName}
                </VegaFont>
              </VegaFlex>
              <VegaFlex direction="col" gap="size-4">
                <VegaFont as="span" color="text-secondary" variant="font-field-label-em">
                  Region
                </VegaFont>
                <VegaFont as="span" variant="font-p2-short">
                  Horizon {detailsRow.region}
                </VegaFont>
              </VegaFlex>
              <VegaFlex direction="col" gap="size-4">
                <VegaFont as="span" color="text-secondary" variant="font-field-label">
                  Application ID
                </VegaFont>
                <VegaFont as="span" variant="font-p2-short">
                  {detailsRow.appUid}
                </VegaFont>
              </VegaFlex>
            </VegaGrid>
          )}
        </DetailsModal>
      </VegaFlex>
    </VegaCard>
  );
};
